import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Loader2, Trash2, ShieldAlert, Ban, CircleCheck } from "lucide-react";
import { errMsg } from "./apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatEquipe({ team, onClose }) {
  const [data, setData] = useState({ messages: [], joueurs: [] });
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [cible, setCible] = useState("");
  const [avert, setAvert] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/teams/${team.id}/chat`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement du chat"));
  }, [team.id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  // Fermeture via Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const envoyer = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/teams/${team.id}/chat`, { contenu: message }, { withCredentials: true });
      setMessage("");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Erreur d'envoi"));
    } finally {
      setSending(false);
    }
  };

  const supprimer = async (msgId) => {
    await axios.delete(`${API}/admin/teams/${team.id}/chat/${msgId}`, { withCredentials: true });
    toast.success("Message supprimé");
    load();
  };

  const avertir = async () => {
    if (!cible || !avert.trim()) return;
    try {
      await axios.post(`${API}/admin/teams/${team.id}/avertir`, { registration_id: cible, contenu: avert }, { withCredentials: true });
      toast.success("Avertissement publié dans le chat");
      setAvert("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Avertissement impossible"));
    }
  };

  const toggleBlocage = async (j) => {
    try {
      await axios.patch(`${API}/admin/registrations/${j.id}`, { chat_bloque: !j.chat_bloque }, { withCredentials: true });
      toast.success(j.chat_bloque ? `${j.prenom} ${j.nom} débloqué du chat` : `${j.prenom} ${j.nom} bloqué du chat`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Action impossible"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid={`chat-modal-${team.id.slice(0, 8)}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[#121214] border border-[#C5A059]/40 w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 lg:p-8">
        <div className="flex items-center justify-between mb-5">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Chat — {team.nom}</p>
          <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors" aria-label="Fermer"><X size={18} /></button>
        </div>

        {/* Modération des joueurs */}
        <div className="border border-[#27272A] bg-[#0A0A0C] p-4 mb-5 space-y-3" data-testid="chat-moderation">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Modération</p>
          <div className="flex flex-wrap gap-2">
            <select value={cible} onChange={(e) => setCible(e.target.value)} data-testid="chat-moderation-joueur"
              className="bg-[#121214] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059] flex-1 min-w-[180px]">
              <option value="">Choisir un joueur…</option>
              {data.joueurs.map((j) => (
                <option key={j.id} value={j.id}>{j.prenom} {j.nom}{j.chat_bloque ? " (bloqué)" : ""}</option>
              ))}
            </select>
            {cible && (() => {
              const j = data.joueurs.find((x) => x.id === cible);
              return (
                <button onClick={() => toggleBlocage(j)} data-testid="chat-moderation-blocage"
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${j?.chat_bloque ? "border-emerald-400/50 text-emerald-300 hover:bg-emerald-400 hover:text-black" : "border-red-400/50 text-red-300 hover:bg-red-400 hover:text-black"}`}>
                  {j?.chat_bloque ? <><CircleCheck size={12} /> Débloquer</> : <><Ban size={12} /> Bloquer du chat</>}
                </button>
              );
            })()}
          </div>
          <div className="flex gap-2">
            <input value={avert} onChange={(e) => setAvert(e.target.value)} maxLength={300}
              placeholder="Avertissement bref, visible par toute l'équipe…" data-testid="chat-moderation-avertissement"
              className="bg-[#121214] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059] flex-1" />
            <button onClick={avertir} disabled={!cible || !avert.trim()} data-testid="chat-moderation-avertir"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-2 border border-amber-400/60 text-amber-300 hover:bg-amber-400 hover:text-black transition-colors disabled:opacity-40">
              <ShieldAlert size={12} /> Avertir
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-2.5 mb-5" data-testid="chat-admin-messages">
          {data.messages.length === 0 && (
            <p className="border border-[#27272A] p-5 text-center text-[#71717A] text-sm">Aucun message dans ce chat pour le moment.</p>
          )}
          {data.messages.map((m) => (
            <div key={m.id} data-testid={`chat-admin-msg-${m.id.slice(0, 8)}`}
              className={`group flex items-start gap-3 p-3.5 border ${m.type === "avertissement" ? "border-red-400/50 bg-red-400/5" : m.auteur_type === "admin" ? "border-[#C5A059]/40 bg-[#C5A059]/5" : "border-[#27272A] bg-[#0A0A0C]"}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-[9px] uppercase tracking-widest ${m.type === "avertissement" ? "text-red-300" : m.auteur_type === "admin" ? "text-[#C5A059]" : "text-[#71717A]"}`}>
                  {m.type === "avertissement" ? `Avertissement${m.cible_nom ? ` — ${m.cible_nom}` : ""}` : m.auteur_nom}
                </p>
                <p className="text-sm text-white mt-1 whitespace-pre-wrap">{m.contenu}</p>
                <p className="text-[#3f3f46] text-[10px] mt-1">{new Date(m.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
              </div>
              <button onClick={() => supprimer(m.id)} data-testid={`chat-admin-delete-${m.id.slice(0, 8)}`}
                className="text-[#3f3f46] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100" aria-label="Supprimer le message">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Message du staff */}
        <form onSubmit={envoyer} className="flex gap-2">
          <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500}
            placeholder="Écrire en tant que Staff D2R…" data-testid="chat-admin-input"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] flex-1" />
          <button type="submit" disabled={sending || !message.trim()} data-testid="chat-admin-send"
            className="flex items-center justify-center bg-[#C5A059] text-black px-5 hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </form>
      </div>
    </div>
  );
}
